import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Typography
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import DatePicker from '../common/DatePicker/DatePicker';
import { FilterFieldSx, TextFeaildSx, CSS_PRIMARY } from '../../../constants/style';
import { saveAMC, deleteAMC } from '../../../apis/AMC';
import { Save, Trash } from 'lucide-react';
import dayjs from 'dayjs';
import { generate12DigitAlphaNumericCode, formatDate } from '../../../utils/MasterUtils';

const AddAmc = ({
  installationId,
  amcEndDate,
  tempAmcEndDate,
  amcDetails,
  userAccess,
  onApprove,
  onSuccess
}) => {
  const [formValues, setFormValues] = useState({
    billNo: '',
    paidDate: dayjs(),
    paidAmount: null,
    amcStartDate: null,
    days: 0,
    amcEndDate: null,
    remarks: '',
    referenceId: '',
    amcGUID: 0,
    installationGUID: installationId,
    isTemporary: false
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEditMode = Boolean(amcDetails);
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isStartDateReadOnly = amcEndDate;
  const [initialyCalled, setInitialyCalled] = useState(false);

  const isEditAllowed = isEditMode ? userAccess?.hasEditPermission : userAccess?.hasAddPermission;

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field) => (date) => {
    setFormValues((prev) => ({ ...prev, [field]: date }));
  };

  const handleGenerate = () => {
    setFormValues((prev) => ({
      ...prev,
      referenceId: generate12DigitAlphaNumericCode()
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    const successMsg = installationId ? 'AMC updated successfully' : 'AMC added successfully';

    const payload = {
      ...formValues,
      amcStartDate: formatDate(formValues.amcStartDate),
      amcEndDate: formatDate(formValues.amcEndDate),
      paidDate: formatDate(formValues.paidDate)
    };

    const response = await saveAMC(payload, successMsg, '');

    if (response.success) {
      onSuccess?.(payload);
    }

    setSaving(false);
  };

  useEffect(() => {
    if (formValues.isTemporary) {
      if (initialyCalled) {
        const endDate = tempAmcEndDate
          ? dayjs(tempAmcEndDate)
          : amcEndDate
          ? dayjs(amcEndDate)
          : dayjs();

        setFormValues((prev) => ({
          ...prev,
          billNo: '',
          paidDate: formatDate(dayjs()),
          paidAmount: null,
          amcStartDate: endDate // ✅ keep as dayjs
        }));
      }
    } else {
      const endDate = amcEndDate ? dayjs(amcEndDate).toDate() : dayjs().toDate();

      setFormValues((prev) => ({
        ...prev,
        days: 365,
        amcStartDate: endDate // ✅ keep as dayjs
      }));
    }
  }, [formValues.isTemporary]);

  useEffect(() => {
    if (initialyCalled) {
      setFormValues((prev) => ({
        ...prev,
        amcEndDate: dayjs(formValues.amcStartDate).add(formValues.days, 'day').toDate()
      }));
      handleGenerate();
    }
  }, [formValues.days]);

  useEffect(() => {
    if (initialyCalled) {
      setFormValues((prev) => ({
        ...prev,
        days: dayjs(formValues.amcEndDate).diff(dayjs(formValues.amcStartDate), 'day')
      }));
      handleGenerate();
    }
  }, [formValues.amcStartDate]);

  useEffect(() => {
    if (!isEditMode) {
      const endDate = amcEndDate ? dayjs(amcEndDate).toDate() : dayjs().toDate();

      setFormValues((prev) => ({
        ...prev,
        days: 365,
        amcStartDate: endDate, // ✅ keep as dayjs
        amcEndDate: dayjs(endDate).add(365, 'day').toDate()
      }));
      handleGenerate();
    }
  }, []);

  // Mark initialization complete only after key fields (dates + referenceId) are populated
  useEffect(() => {
    if (!initialyCalled && formValues.amcStartDate && formValues.amcEndDate) {
      setInitialyCalled(true);
      handleGenerate();
    }
  }, [
    formValues.referenceId,
    formValues.amcStartDate,
    formValues.amcEndDate,
    isEditMode,
    initialyCalled
  ]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `AMC deleted successfully`;
      const response = await deleteAMC(amcDetails?.amcGUID, successMsg, '');
      if (response && response.success) {
        onSuccess?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      const dateFormated = {
        ...amcDetails,
        paidDate: amcDetails.paidDate ? dayjs(amcDetails.paidDate).toDate() : null,
        amcStartDate: amcDetails.amcStartDate ? dayjs(amcDetails.amcStartDate).toDate() : null,
        amcEndDate: amcDetails.amcEndDate ? dayjs(amcDetails.amcEndDate).toDate() : null,
        installationGUID: installationId ?? 0,
        days:
          amcDetails.amcStartDate && amcDetails.amcEndDate
            ? dayjs(amcDetails.amcEndDate).diff(dayjs(amcDetails.amcStartDate), 'day')
            : 0
      };
      setFormValues({
        ...formValues,
        ...dateFormated
      });
    }
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          flex: '1 1 50%',
          minWidth: '200px',
          display: `${isEditMode ? 'none' : 'flex'}`,
          alignItems: 'center',
          justifyContent: 'end',
          mr: 1,
          py: 0,
          minHeight: '20px'
        }}
      >
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Checkbox
              checked={formValues.isTemporary}
              onChange={handleChange('isTemporary')}
              size="small"
              sx={{
                p: 0,
                color: 'primary.main',
                '&.Mui-checked': { color: CSS_PRIMARY }
              }}
              readOnly={!isEditAllowed}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Temporary AMC
            </Typography>
          }
        />
      </Box>
      {/* Top Section - Bill Details */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          alignItems: 'start'
        }}
      >
        <TextField
          label="AMC Bill No *"
          fullWidth
          size="small"
          value={formValues.billNo}
          onChange={handleChange('billNo')}
          sx={FilterFieldSx}
          disabled={formValues.isTemporary}
          slotProps={{
            input: {
              readOnly: !isEditAllowed
            }
          }}
        />
        <DatePicker
          mode="single"
          label="AMC Bill Date *"
          value={formValues.paidDate}
          onChange={handleDateChange('paidDate')}
          height={36}
          width="100%"
          disabled={formValues.isTemporary}
          readOnly={!isEditAllowed}
        />
        <NumericFormat
          value={formValues.paidAmount}
          disabled={formValues.isTemporary || !isEditAllowed}
          onValueChange={(values) => {
            handleChange('paidAmount')({
              target: {
                name: 'paidAmount',
                value: values.floatValue
              }
            });
          }}
          decimalScale={4}
          customInput={TextField}
          thousandSeparator
          prefix="₹"
          variant="outlined"
          label="AMC Bill Amount *"
          fullWidth
          size="small"
          sx={FilterFieldSx}
        />
      </Box>

      <Divider />

      {/* Middle Section - Date and Days */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          alignItems: 'start'
        }}
      >
        <DatePicker
          mode="single"
          label="Start Date"
          value={formValues.amcStartDate}
          onChange={handleDateChange('amcStartDate')}
          height={36}
          width="100%"
          readOnly={isStartDateReadOnly || !isEditAllowed}
          maxDate={formValues.amcEndDate}
        />

        <DatePicker
          mode="single"
          label="End Date"
          value={formValues.amcEndDate}
          onChange={handleDateChange('amcEndDate')}
          height={36}
          width="100%"
          readOnly={true}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          alignItems: 'start'
        }}
      >
        <TextField
          label="No Of Days"
          fullWidth
          size="small"
          value={formValues.days}
          onChange={(e) => {
            const value = Number(e.target.value);

            if (value <= 366 || e.target.value === '') {
              handleChange('days')(e);
            }
          }}
          sx={{ ...FilterFieldSx, flex: 1 }}
          slotProps={{
            input: {
              readOnly: !isEditAllowed
            }
          }}
        />
        <TextField
          label="Reference Id"
          fullWidth
          size="small"
          value={formValues.referenceId}
          onChange={handleChange('referenceId')}
          sx={{ ...FilterFieldSx, flex: 2 }}
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
      </Box>
      {/* Remarks Section */}
      <Box>
        <TextField
          label="Remarks"
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={formValues.remarks}
          onChange={handleChange('remarks')}
          slotProps={{
            input: {
              readOnly: !isEditAllowed
            }
          }}
          sx={TextFeaildSx}
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={
            deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
          }
          disabled={deleting}
          onClick={handleDelete}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 100,
            background: '#e05757',
            '&:hover': { background: '#6d6464' },
            boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
            display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex',
            mr: 2
          }}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={saving ? <CircularProgress size={14} /> : <Save size={15} />}
          disabled={saving}
          sx={{
            minWidth: 160,
            textTransform: 'none',
            fontWeight: 600,
            background: '#337ab7', // Matching the classic blue from the screenshot
            '&:hover': { background: '#286090' },
            display: !isEditAllowed ? 'none' : 'inline-flex'
          }}
        >
          Send Request
        </Button>
      </Box>
    </Box>
  );
};

export default AddAmc;
